import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MapPin, X } from 'lucide-react';
import { AUSTRIAN_STATES, getDistrictsForState } from '../../data/austrianLocations';

/**
 * Combined preferred-states badge toggles + add specific (state, district) work locations.
 *
 * Props:
 *   preferredStates: string[] -> currently selected Bundesländer
 *   preferredWorkLocations: [{state, district}] -> specific entries
 *   onToggleState(state): toggles a Bundesland in the badge row
 *   onAddLocation({state, district}): appends a new entry (parent dedupes)
 *   onRemoveLocation(idx): removes entry at idx
 *   variant: "onboarding" | "dashboard" - drives styling differences
 *   testIdPrefix: data-testid prefix
 *   showAddButton: bool - render the add-row (default true)
 */
export const PreferredWorkLocationsField = ({
  preferredStates = [],
  preferredWorkLocations = [],
  onToggleState,
  onAddLocation,
  onRemoveLocation,
  variant = 'onboarding',
  testIdPrefix = 'preferred',
  showAddButton = true,
}) => {
  const [draft, setDraft] = useState({ state: '', district: '' });
  const isOnboarding = variant === 'onboarding';
  const stateBtnSize = isOnboarding ? 'px-4 py-2' : 'px-3 py-1.5';
  const containerCls = isOnboarding
    ? 'bg-slate-50 p-4 rounded-xl'
    : '';

  const handleAdd = () => {
    if (!draft.state) return;
    onAddLocation({ ...draft });
    setDraft({ state: '', district: '' });
  };

  return (
    <div className="space-y-2">
      {/* Bundesland badge toggles */}
      <div className="flex flex-wrap gap-2 mb-4">
        {AUSTRIAN_STATES.map((state) => {
          const active = preferredStates.includes(state);
          return (
            <button
              key={state}
              type="button"
              onClick={() => onToggleState(state)}
              className={`${stateBtnSize} rounded-full text-sm transition-all ${
                active
                  ? `${isOnboarding ? 'bg-primary' : 'bg-blue-500'} text-white shadow-lg`
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              data-testid={`${testIdPrefix}-state-${state}`}
            >
              {state}
            </button>
          );
        })}
      </div>

      {/* Specific work locations */}
      {showAddButton && (
        <div className={containerCls}>
          {isOnboarding && (
            <>
              <Label className="text-sm font-semibold mb-2 block">Spezifische Arbeitsorte (optional)</Label>
              <p className="text-xs text-slate-500 mb-3">Füge bestimmte Bezirke hinzu, wenn du genauer sein möchtest</p>
            </>
          )}
          {!isOnboarding && (
            <p className="text-xs text-slate-500 mb-2">Füge spezifische Bezirke hinzu, in denen du arbeiten möchtest</p>
          )}

          {/* Existing entries */}
          {preferredWorkLocations.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {preferredWorkLocations.map((loc, idx) => (
                isOnboarding ? (
                  <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm">
                    <MapPin className="w-3 h-3" />
                    {loc.district ? `${loc.district}, ${loc.state}` : loc.state}
                    <button
                      type="button"
                      onClick={() => onRemoveLocation(idx)}
                      className="ml-1 hover:text-red-500"
                      data-testid={`${testIdPrefix}-remove-${idx}`}
                    >
                      ×
                    </button>
                  </span>
                ) : (
                  <Badge key={idx} variant="secondary" className="flex items-center gap-1 py-1">
                    <MapPin className="w-3 h-3" />
                    {loc.district ? `${loc.district}, ${loc.state}` : loc.state}
                    <button
                      type="button"
                      onClick={() => onRemoveLocation(idx)}
                      className="ml-1 hover:text-red-500"
                      data-testid={`${testIdPrefix}-remove-${idx}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )
              ))}
            </div>
          )}

          {/* Add new entry */}
          <div className="flex gap-2">
            <Select
              value={draft.state || undefined}
              onValueChange={(v) => setDraft({ state: v, district: '' })}
            >
              <SelectTrigger className={`flex-1 ${isOnboarding ? 'h-10 rounded-lg' : ''}`} data-testid={`${testIdPrefix}-draft-state`}>
                <SelectValue placeholder="Bundesland" />
              </SelectTrigger>
              <SelectContent className="z-[100]">
                {AUSTRIAN_STATES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={draft.district || '__all__'}
              onValueChange={(v) =>
                setDraft({ ...draft, district: v === '__all__' ? '' : v })
              }
              disabled={!draft.state}
            >
              <SelectTrigger className={`flex-1 ${isOnboarding ? 'h-10 rounded-lg' : ''}`} data-testid={`${testIdPrefix}-draft-district`}>
                <SelectValue placeholder={draft.state ? 'Bezirk wählen' : 'Erst Bundesland'} />
              </SelectTrigger>
              <SelectContent className="z-[100]">
                <SelectItem value="__all__">-- Ganzes Bundesland --</SelectItem>
                {getDistrictsForState(draft.state).map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              className={isOnboarding ? 'h-10 px-4' : ''}
              onClick={handleAdd}
              data-testid={`${testIdPrefix}-add-button`}
            >
              {isOnboarding ? 'Hinzufügen' : '+'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
